import {
  ExamMarketStatus,
  ProvidersList,
} from "#/pages/exam-market/management";
import { auth } from "#/utils";
import {
  Button,
  ComboBox,
  Datepicker,
  type TCalendarValue,
  TextField,
  dayjs,
} from "@monta/design-system";
import { Filter } from "@monta/icons";
import clsx from "clsx";
import { type FormikConfig, useFormik } from "formik";
import { useMemo } from "react";
import styles from "./SearchForm.module.scss";

export interface SearchFormProps {
  onSubmit: FormikConfig<IExamMarketManagementForm>["onSubmit"];
}

export interface IExamMarketManagementForm {
  productName?: string;
  fromDate?: string;
  toDate?: string;
  statusId?: number;
  providerId?: number;
}

const SESSION_STORAGE_KEY = "examMarketFilterForm";

const initialValuesString = sessionStorage.getItem(SESSION_STORAGE_KEY);
export let initialValues = initialValuesString
  ? JSON.parse(initialValuesString)
  : {};
const isAdmin = () => auth.user?.id === 1120727; // SIMA USER ID

const SearchForm = (props: SearchFormProps) => {
  const { onSubmit } = props;
  const {
    getFieldProps,
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    submitForm,
  } = useFormik<IExamMarketManagementForm>({
    initialValues,
    onSubmit(values, helpers) {
      onSubmit(values, helpers);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(values));
    },
  });

  const datepickerValue = useMemo<TCalendarValue>(
    () => [
      values.fromDate ? dayjs(values.fromDate) : null,
      values.toDate ? dayjs(values.toDate) : null,
    ],
    [values.fromDate, values.toDate],
  );

  const ProvidersListItems = useMemo(
    () => ProvidersList.map((i) => ({ label: i.name, value: String(i.id) })),
    [],
  );
  const ExamMarketStatusItems = useMemo(
    () => ExamMarketStatus.map((i) => ({ label: i.fa, value: String(i.id) })),
    [],
  );

  return (
    <div className={styles["wrapper"]}>
      <h2>فیلتر ها</h2>
      <form
        onSubmit={handleSubmit}
        className={clsx(
          styles["wrapper__form"],
          isAdmin() && styles["wrapper__form--admin"],
        )}
      >
        {isAdmin() && (
          <ComboBox
            items={ProvidersListItems}
            placeholder="نام تامیین کننده"
            innerbefore={
              <Filter className={styles["wrapper__form__filter-icon"]} />
            }
            {...getFieldProps("providerId")}
            onChange={(value) => setFieldValue("providerId", value)}
          />
        )}
        <TextField
          placeholder="نام محصول مورد نظر را وارد کنید"
          value={values.productName || ""}
          onChange={(e) => setFieldValue("productName", e.target.value)}
        />
        <Datepicker
          placeholder="بازه زمانی مورد نظر را انتخاب کنید"
          range
          value={datepickerValue}
          onChange={(value) => {
            if (!Array.isArray(value)) return;
            setFieldValue("fromDate", value[0]?.toString());
            setFieldValue("toDate", value[1]?.toString());
          }}
        />
        <ComboBox
          items={ExamMarketStatusItems}
          placeholder="وضعیت مورد نظر را انتخاب کنید"
          innerbefore={
            <Filter className={styles["wrapper__form__filter-icon"]} />
          }
          {...getFieldProps("statusId")}
          onChange={(value) => setFieldValue("statusId", value)}
        />
        <Button type="submit">اعمال فیلتر</Button>
        <Button
          onClick={() => {
            setValues({});
            initialValues = {};
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            submitForm();
          }}
        >
          حذف فیلتر
        </Button>
      </form>
    </div>
  );
};

export default SearchForm;
